import {
  index,
  integer,
  primaryKey,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { DEFAULT_TIMEZONE } from "@/config/constants";
import { uuidv7 } from "../server/helpers/general";

// ─── Auth tables (better-auth) ───────────────────────────────────────────────

export const user = sqliteTable("user", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  email: text("email").notNull().unique(),
  emailVerified: integer("email_verified").notNull().default(0),
  image: text("image"),
  createdAt: text("created_at").notNull(),
  updatedAt: text("updated_at").notNull(),
});

export const session = sqliteTable("session", {
  id: text("id").primaryKey(),
  expiresAt: text("expires_at").notNull(),
  token: text("token").notNull().unique(),
  createdAt: text("created_at").notNull(),
  updatedAt: text("updated_at").notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  userId: text("user_id")
    .notNull()
    .references(() => user.id),
});

export const account = sqliteTable("account", {
  id: text("id").primaryKey(),
  accountId: text("account_id").notNull(),
  providerId: text("provider_id").notNull(),
  userId: text("user_id")
    .notNull()
    .references(() => user.id),
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  idToken: text("id_token"),
  accessTokenExpiresAt: text("access_token_expires_at"),
  refreshTokenExpiresAt: text("refresh_token_expires_at"),
  scope: text("scope"),
  password: text("password"),
  createdAt: text("created_at").notNull(),
  updatedAt: text("updated_at").notNull(),
});

export const verification = sqliteTable("verification", {
  id: text("id").primaryKey(),
  identifier: text("identifier").notNull(),
  value: text("value").notNull(),
  expiresAt: text("expires_at").notNull(),
  createdAt: text("created_at"),
  updatedAt: text("updated_at"),
});

// ─── Organizations ────────────────────────────────────────────────────────────

export const organizations = sqliteTable("organizations", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  createdAt: text("created_at")
    .notNull()
    .$defaultFn(() => new Date().toISOString()),
});

// ─── Groups ───────────────────────────────────────────────────────────────────

export const groups = sqliteTable(
  "groups",
  {
    id: text("id").primaryKey(),
    orgId: text("org_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    slug: text("slug").notNull(),
    type: text("type").notNull().default("custom"),
    parentId: text("parent_id"),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [
    uniqueIndex("idx_groups_org_slug").on(t.orgId, t.slug),
    index("idx_groups_org_id").on(t.orgId),
    index("idx_groups_parent_id").on(t.parentId),
  ],
);

export const groupMembers = sqliteTable(
  "group_members",
  {
    groupId: text("group_id")
      .notNull()
      .references(() => groups.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    role: text("role").notNull().default("member"),
    joinedAt: text("joined_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [
    primaryKey({ columns: [t.groupId, t.userId] }),
    index("idx_group_members_user_id").on(t.userId),
  ],
);

// ─── Application tables ──────────────────────────────────────────────────────

export const profiles = sqliteTable(
  "profiles",
  {
    id: text("id").primaryKey(),
    username: text("username").notNull().unique(),
    timezone: text("timezone").notNull().default(DEFAULT_TIMEZONE),
    orgId: text("org_id").references(() => organizations.id),
    orgRole: text("org_role"),
  },
  (t) => [index("idx_profiles_org_id").on(t.orgId)],
);

export const competitions = sqliteTable(
  "competitions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => uuidv7()),
    ownerUserId: text("owner_user_id")
      .notNull()
      .references(() => profiles.id),
    name: text("name").notNull(),
    description: text("description"),
    type: text("type").notNull().default("step_race"),
    teamScoring: text("team_scoring").notNull().default("sum"),
    startDate: text("start_date").notNull(),
    endDate: text("end_date").notNull(),
    targetSteps: integer("target_steps"),
    dailyGoalSteps: integer("daily_goal_steps"),
    timezone: text("timezone").notNull().default(DEFAULT_TIMEZONE),
    inviteCode: text("invite_code").notNull().unique(),
    visibility: text("visibility").notNull().default("invite-only"),
    scopeMode: text("scope_mode"),
    orgId: text("org_id").references(() => organizations.id),
    rematchCompetitionId: text("rematch_competition_id"),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    deletedAt: text("deleted_at"),
  },
  (t) => [
    index("idx_competitions_owner_user_id").on(t.ownerUserId),
    index("idx_competitions_deleted_at").on(t.deletedAt),
    index("idx_competitions_org_id").on(t.orgId),
    index("idx_competitions_rematch_competition_id").on(t.rematchCompetitionId),
  ],
);

export const competitionMembers = sqliteTable(
  "competition_members",
  {
    competitionId: text("competition_id")
      .notNull()
      .references(() => competitions.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    role: text("role").notNull().default("member"),
    dailyGoalSteps: integer("daily_goal_steps"),
    joinedAt: text("joined_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [
    primaryKey({ columns: [t.competitionId, t.userId] }),
    index("idx_competition_members_user_id").on(t.userId),
  ],
);

export const competitionBadges = sqliteTable(
  "competition_badges",
  {
    id: text("id").primaryKey(),
    competitionId: text("competition_id")
      .notNull()
      .references(() => competitions.id, { onDelete: "cascade" }),
    badgeId: text("badge_id").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [index("idx_competition_badges_competition_id").on(t.competitionId)],
);

export const friendships = sqliteTable(
  "friendships",
  {
    requesterUserId: text("requester_user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    addresseeUserId: text("addressee_user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("pending"),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [primaryKey({ columns: [t.requesterUserId, t.addresseeUserId] })],
);

export const invites = sqliteTable(
  "invites",
  {
    id: text("id").primaryKey(),
    competitionId: text("competition_id")
      .notNull()
      .references(() => competitions.id, { onDelete: "cascade" }),
    createdByUserId: text("created_by_user_id")
      .notNull()
      .references(() => profiles.id),
    inviteCode: text("invite_code").notNull(),
    inviteeEmail: text("invitee_email"),
    expiresAt: text("expires_at"),
    acceptedAt: text("accepted_at"),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [index("idx_invites_competition_id").on(t.competitionId)],
);

export const providerConnections = sqliteTable(
  "provider_connections",
  {
    id: text("id").primaryKey(),
    userId: text("user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    provider: text("provider").notNull(),
    providerUserId: text("provider_user_id"),
    accessTokenEncrypted: text("access_token_encrypted"),
    refreshTokenEncrypted: text("refresh_token_encrypted"),
    tokenExpiresAt: text("token_expires_at"),
    lastSyncAt: text("last_sync_at"),
    status: text("status").notNull().default("not_connected"),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: text("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [index("idx_provider_connections_user_id").on(t.userId)],
);

export const dailyStepTotals = sqliteTable(
  "daily_step_totals",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => uuidv7()),
    userId: text("user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    provider: text("provider").notNull(),
    stepDate: text("step_date").notNull(),
    steps: integer("steps").notNull(),
    syncedAt: text("synced_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [index("idx_daily_step_totals_user_date").on(t.userId, t.stepDate)],
);

export const competitionComments = sqliteTable(
  "competition_comments",
  {
    id: text("id").primaryKey(),
    competitionId: text("competition_id")
      .notNull()
      .references(() => competitions.id, { onDelete: "cascade" }),
    authorUserId: text("author_user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    parentId: text("parent_id"),
    body: text("body").notNull(),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: text("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [
    index("idx_competition_comments_competition_id").on(t.competitionId),
    index("idx_competition_comments_parent_id").on(t.parentId),
  ],
);

export const commentReactions = sqliteTable(
  "comment_reactions",
  {
    id: text("id").primaryKey(),
    commentId: text("comment_id")
      .notNull()
      .references(() => competitionComments.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    emoji: text("emoji").notNull(),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [index("idx_comment_reactions_comment_id").on(t.commentId)],
);

export const teams = sqliteTable(
  "teams",
  {
    id: text("id").primaryKey(),
    competitionId: text("competition_id")
      .notNull()
      .references(() => competitions.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    createdByUserId: text("created_by_user_id")
      .notNull()
      .references(() => profiles.id),
    orgId: text("org_id").references(() => organizations.id),
    groupId: text("group_id").references(() => groups.id, { onDelete: "set null" }),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [
    index("idx_teams_competition").on(t.competitionId),
    index("idx_teams_org_id").on(t.orgId),
    index("idx_teams_group_id").on(t.groupId),
  ],
);

export const teamMembers = sqliteTable(
  "team_members",
  {
    teamId: text("team_id")
      .notNull()
      .references(() => teams.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    role: text("role").notNull().default("member"),
    joinedAt: text("joined_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [
    primaryKey({ columns: [t.teamId, t.userId] }),
    index("idx_team_members_user").on(t.userId),
  ],
);

export const competitionGroups = sqliteTable(
  "competition_groups",
  {
    competitionId: text("competition_id")
      .notNull()
      .references(() => competitions.id, { onDelete: "cascade" }),
    groupId: text("group_id")
      .notNull()
      .references(() => groups.id, { onDelete: "cascade" }),
  },
  (t) => [
    primaryKey({ columns: [t.competitionId, t.groupId] }),
    index("idx_competition_groups_group_id").on(t.groupId),
  ],
);

export const badgeDefinitions = sqliteTable("badge_definitions", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description").notNull(),
  iconEmoji: text("icon_emoji").notNull(),
  stepThreshold: integer("step_threshold").notNull(),
  tier: text("tier").notNull(),
  createdAt: text("created_at")
    .notNull()
    .$defaultFn(() => new Date().toISOString()),
});

export const notificationPreferences = sqliteTable(
  "notification_preferences",
  {
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    competitionEnd: integer("competition_end").notNull().default(1),
    friendRequest: integer("friend_request").notNull().default(1),
    invitationAccepted: integer("invitation_accepted").notNull().default(1),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: text("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [primaryKey({ columns: [t.userId] })],
);

export const pushSubscriptions = sqliteTable(
  "push_subscriptions",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    endpoint: text("endpoint").notNull(),
    p256dhKey: text("p256dh_key").notNull(),
    authKey: text("auth_key").notNull(),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: text("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [
    uniqueIndex("idx_push_subscriptions_user_endpoint").on(t.userId, t.endpoint),
    index("idx_push_subscriptions_user_id").on(t.userId),
  ],
);

export const expoPushTokens = sqliteTable(
  "expo_push_tokens",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    token: text("token").notNull(),
    platform: text("platform").notNull(),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: text("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [
    uniqueIndex("idx_expo_push_tokens_user_token").on(t.userId, t.token),
    index("idx_expo_push_tokens_user_id").on(t.userId),
  ],
);

export const dailyMessages = sqliteTable(
  "daily_messages",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => uuidv7()),
    competitionId: text("competition_id")
      .notNull()
      .references(() => competitions.id, { onDelete: "cascade" }),
    date: text("date").notNull(),
    body: text("body").notNull(),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [uniqueIndex("idx_daily_messages_comp_date").on(t.competitionId, t.date)],
);

export const competitionSummaries = sqliteTable(
  "competition_summaries",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => uuidv7()),
    competitionId: text("competition_id")
      .notNull()
      .references(() => competitions.id, { onDelete: "cascade" }),
    body: text("body").notNull(),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [uniqueIndex("idx_competition_summaries_comp").on(t.competitionId)],
);

export const commentMentions = sqliteTable(
  "comment_mentions",
  {
    id: text("id").primaryKey(),
    commentId: text("comment_id")
      .notNull()
      .references(() => competitionComments.id, { onDelete: "cascade" }),
    mentionedUserId: text("mentioned_user_id")
      .notNull()
      .references(() => profiles.id, { onDelete: "cascade" }),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [index("idx_comment_mentions_comment_id").on(t.commentId)],
);

// ─── Invite-only access ───────────────────────────────────────────────────────

export const accessInvites = sqliteTable(
  "access_invites",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => uuidv7()),
    code: text("code").notNull().unique(),
    createdByUserId: text("created_by_user_id").references(() => user.id, { onDelete: "set null" }),
    redeemedByUserId: text("redeemed_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    redeemedAt: text("redeemed_at"),
    expiresAt: text("expires_at"),
    revokedAt: text("revoked_at"),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [index("idx_access_invites_created_by_user_id").on(t.createdByUserId)],
);

export const waitlistEntries = sqliteTable("waitlist_entries", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => uuidv7()),
  email: text("email").notNull().unique(),
  name: text("name"),
  createdAt: text("created_at")
    .notNull()
    .$defaultFn(() => new Date().toISOString()),
});

// Client-reported errors (e.g. Health Connect / Google Health connection
// failures), persisted as a fallback channel so they're still queryable
// when Sentry is unreachable or misconfigured on a device.
export const clientErrorLogs = sqliteTable(
  "client_error_logs",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => uuidv7()),
    userId: text("user_id").references(() => user.id, { onDelete: "set null" }),
    source: text("source").notNull(),
    stage: text("stage").notNull(),
    message: text("message").notNull(),
    stack: text("stack"),
    platformOs: text("platform_os"),
    platformVersion: text("platform_version"),
    extra: text("extra"),
    createdAt: text("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  (t) => [
    index("idx_client_error_logs_user_id").on(t.userId),
    index("idx_client_error_logs_created_at").on(t.createdAt),
  ],
);
